import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Program, ProgramType, getProgramTypeGlyph, getProgramTypeIcon, getProgramTypeColor, getProgramTypeDisplayName, isEnrollmentLocked } from '../types/program';
import { useProgramTypeName } from '../config/useLabels';
import { CalendarDays, ChevronLeft, ChevronRight, List as ListIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ProgramCard } from '../components/ProgramCard';
import { SectionGlyph } from '../components/SectionGlyph';
import { Navigate } from 'react-router-dom';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useBrandProfile } from '../contexts/brand-profile';
import { brandProfileOf, brandSupportsView, defaultViewFor, type CatalogView } from '../config/brandProfiles';
import { ProgramCalendarView, type CalendarProgram } from '../components/ProgramCalendarView';
import { CampPaymentNudge } from '../components/CampPaymentNudge';
import { useUnpaidInvoiceNudge } from '../hooks/useUnpaidInvoiceNudge';
import { PROGRAM_LOCATION_EMBED } from '../types/location';
import { discountedPriceOf } from '../utils/eligibilityDiscounts';
import { useProgramsCapacity } from '../hooks/useProgramSeats';
import { isProgramFull } from '../services/programWaitlist';

interface LineItemConfig {
  id: string;
  name: string;
  type?: string;
  description?: string;
  subtype?: string;
}

interface PricingItem {
  amount: number;
  is_optional: boolean;
  line_item_configs: LineItemConfig | LineItemConfig[];
}

interface ProgramWithPricing extends Program {
  pricing?: PricingItem[];
  tags?: string[] | null;
}

/** The catalog row shape the calendar needs: this page's row plus its discounted price. */
function toCalendarProgram(program: ProgramWithPricing): CalendarProgram {
  const discounted = discountedPriceOf(
    program.price,
    (program.pricing ?? []).map((row) => {
      const config = Array.isArray(row.line_item_configs) ? row.line_item_configs[0] : row.line_item_configs;
      return { amount: row.amount, subtype: config?.subtype };
    })
  );
  return {
    id: program.id,
    name: program.name,
    type: program.type,
    price: program.price,
    billing_type: program.billing_type,
    duration: program.duration,
    schedule_cron: program.schedule_cron,
    start_date: program.start_date,
    end_date: program.end_date,
    excluded_dates: program.excluded_dates,
    selective_dates: program.selective_dates,
    enrollment_status: program.enrollment_status,
    location: program.location,
    discounted: discounted ? { holderLabel: discounted.discount.holderLabel, price: discounted.price } : null,
  };
}

export function Programs() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const programTypeName = useProgramTypeName();
  const { activeBrand } = useBrandProfile();
  const brandProfile = brandProfileOf(activeBrand);
  // Held per brand rather than globally: BHCS has no calendar, so a parent who chose the
  // calendar in Kaleido must not land on a missing view when they switch back.
  const [viewByBrand, setViewByBrand] = useState<Partial<Record<string, CatalogView>>>({});
  const storedView = viewByBrand[activeBrand];
  const view: CatalogView =
    storedView && brandSupportsView(activeBrand, storedView) ? storedView : defaultViewFor(activeBrand);
  const [isMobile, setIsMobile] = useState(false);
  const [activeType, setActiveType] = useState<string>('');
  const [stickyTop, setStickyTop] = useState<number>(80); // default header height
  const stickyNavRef = useRef<HTMLDivElement | null>(null);
  const [stickyNavHeight, setStickyNavHeight] = useState<number>(48);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const isProgrammaticScrollRef = useRef<boolean>(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  // Check for modern mobile nav
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || /Mobi|Android/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Measure header visibility and compute sticky top offset
  useEffect(() => {
    const computeStickyTop = () => {
      // Modern mobile nav uses fixed 5rem header
      if (isMobile) {
        setStickyTop(80);
        return;
      }
      const header = document.querySelector('nav.nav-fixed-safe');
      const headerElement = header as HTMLElement | null;
      const isHidden = headerElement?.classList.contains('-translate-y-full') ?? false;
      // Align with .content-safe-area which uses 5rem (80px)
      setStickyTop(isHidden ? 0 : 80);
    };

    computeStickyTop();
    const onScroll = () => computeStickyTop();
    const onResize = () => {
      computeStickyTop();
      if (stickyNavRef.current) {
        setStickyNavHeight(Math.round(stickyNavRef.current.getBoundingClientRect().height));
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    // Initial measure of sticky nav height
    if (stickyNavRef.current) {
      setStickyNavHeight(Math.round(stickyNavRef.current.getBoundingClientRect().height));
    }
    return () => {
      window.removeEventListener('scroll', onScroll as EventListener);
      window.removeEventListener('resize', onResize);
    };
  }, [isMobile]);

  const nudgeNav = (dir: 'left' | 'right') => {
    stickyNavRef.current?.scrollBy({ left: dir === 'left' ? -150 : 150, behavior: 'smooth' });
  };

  const { data: programs, isLoading, error } = useQuery<ProgramWithPricing[]>({
    // Brand is part of the key, not a client-side filter on one cached list: the two catalogs
    // are different result sets, and sharing an entry would show the previous brand's programs
    // for as long as the refetch takes.
    queryKey: ['programs-list', activeBrand],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.email) throw new Error('No authenticated user');

        // Get all active programs with pricing data
        const { data, error } = await supabase
          .from('programs')
          .select(`
            id,
            name,
            type,
            tags,
            price,
            billing_type,
            brand_key,
            active,
            enrollment_status,
            capacity,
            start_date,
            end_date,
            schedule_cron,
            duration,
            excluded_dates,
            ${PROGRAM_LOCATION_EMBED},
            selective_dates,
            marketing_metadata,
            created_at,
            updated_at,
            pricing (
              amount,
              is_optional,
              line_item_configs:line_item_config_id (
                id,
                name,
                type,
                description,
                subtype
              )
            )
          `)
          .eq('active', true)
          // Presentation scope, not access control — a parent is never *forbidden* the other
          // brand's rows, they are simply looking at one catalog at a time. The switcher is
          // how they see the other.
          .eq('brand_key', activeBrand)
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Enrollment-closed programs still run (existing students unaffected) but are no
        // longer open to new signups, so they're dropped from the browse catalog entirely.
        return (data as unknown as ProgramWithPricing[]).filter((program) => !isEnrollmentLocked(program));
      } catch (err) {
        console.error('Error fetching programs:', err);
        throw err;
      }
    }
  });

  // A full program stays in the catalog, badged, because its page is where the waitlist is.
  // Asks only about capped programs; with none capped it makes no request.
  const capacityById = useProgramsCapacity(programs);

  // Same query key + shape as Dashboard.tsx and Payments.tsx, so they share the React Query cache
  const { data: parentData } = useQuery<{ id: string } | null>({
    queryKey: ['current-parent', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const { data, error } = await supabase
        .from('parents')
        .select('id')
        .eq('email', user.email)
        .single();
      if (error) return null;
      return data ?? null;
    },
    enabled: !!user?.email,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
  const campNudge = useUnpaidInvoiceNudge({ parentId: parentData?.id });

  // Group and sort programs by type (stable reference)
  const { sortedEntries, typesOrder } = useMemo(() => {
    const list = programs ?? [];
    const grouped = new Map<string, ProgramWithPricing[]>();
    list.forEach((program) => {
      const typeKey = program.type || 'other';
      const existing = grouped.get(typeKey) ?? [];
      existing.push(program);
      grouped.set(typeKey, existing);
    });
    // A brand that declares its own section order gets it; BHCS declares none and keeps the
    // order it has always had (camp first, then alphabetical), so its catalog is untouched.
    const declaredOrder = brandProfile.programTypeOrder;
    const entries = Array.from(grouped.entries()).sort(([a], [b]) => {
      if (declaredOrder) {
        const rank = (t: string) => {
          const i = declaredOrder.indexOf(t);
          return i === -1 ? Number.MAX_SAFE_INTEGER : i;
        };
        return rank(a) - rank(b) || a.localeCompare(b);
      }
      if (a === 'camp') return -1;
      if (b === 'camp') return 1;
      return a.localeCompare(b);
    });
    return { sortedEntries: entries, typesOrder: entries.map(([t]) => t) };
  }, [programs, brandProfile.programTypeOrder]);

  // Initialize refs for each section type
  useEffect(() => {
    const refs: Record<string, HTMLDivElement | null> = {};
    typesOrder.forEach((t) => {
      refs[t] = sectionRefs.current[t] ?? null;
    });
    sectionRefs.current = refs;
  }, [typesOrder]);

  // Track scroll arrows for sticky nav
  useEffect(() => {
    const el = stickyNavRef.current;
    if (!el) return;
    const update = () => {
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [typesOrder]);

  // Scroll-driven active category highlighting for reliable offset handling
  useEffect(() => {
    if (!sortedEntries.length) return;

    let rafId: number | null = null;

    const computeActive = () => {
      if (isProgrammaticScrollRef.current) return;
      const offset = stickyTop + stickyNavHeight + 8;
      const currentY = window.scrollY;
      const thresholdPx = 36; // slightly larger hysteresis to reduce boundary jitter

      let currentType = typesOrder[0];
      for (let i = 0; i < typesOrder.length; i++) {
        const type = typesOrder[i];
        const el = sectionRefs.current[type];
        if (!el) continue;
        const elTop = el.getBoundingClientRect().top + window.scrollY - offset;
        if (currentY + thresholdPx >= elTop) {
          currentType = type;
        } else {
          break;
        }
      }
      setActiveType(currentType);
    };

    const onScroll = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        computeActive();
      });
    };

    const onResize = () => computeActive();

    computeActive();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScroll as EventListener);
      window.removeEventListener('resize', onResize);
    };
  }, [sortedEntries.length, stickyTop, stickyNavHeight, typesOrder]);

  const scrollToType = (type: string) => {
    const el = sectionRefs.current[type];
    if (!el) return;
    const absoluteTop = el.getBoundingClientRect().top + window.scrollY;
    const scrollingDown = absoluteTop > window.scrollY;
    const predictedStickyTop = scrollingDown ? 0 : stickyTop; // header hides when scrolling down
    const y = absoluteTop - (predictedStickyTop + stickyNavHeight + 8);
    isProgrammaticScrollRef.current = true;
    window.scrollTo({ top: y, behavior: 'smooth' });
    // Optimistically set active pill to target to avoid flicker during smooth scroll
    setActiveType(type);
    // Re-enable active detection after scroll settles
    window.setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 500);
  };

  if (isAuthLoading || isLoading) {
    return (
      <div data-testid="loading-skeleton" className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-gray-100 p-6 rounded-lg">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4" />
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-1/3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-red-600">Error loading programs</h2>
        <p className="mt-2 text-gray-600">Please try again later</p>
      </div>
    );
  }

  if (!programs || programs.length === 0) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">
          No {brandProfile.shortName} programs available.
        </h2>
        <p className="mt-2 text-gray-600">Check back later for new programs</p>
      </div>
    );
  }

  /* Brand name and the list/calendar switch. Rendered only for a brand that offers more than
     one view, so the BHCS catalog is byte-identical to what it was before Kaleido existed.
     Shared by both branches below rather than duplicated — the two differ only in where it
     can be placed without the fixed category nav landing on it. */
  const catalogHeader = brandProfile.views.length > 1 ? (
    <div className="mb-4 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
          {brandProfile.shortName} programs
        </h1>
        <p className="text-xs sm:text-sm text-gray-500 truncate">{brandProfile.tagline}</p>
      </div>
      <div
        role="tablist"
        aria-label="Catalog view"
        className="flex-shrink-0 inline-flex rounded-lg border border-gray-300 bg-white p-0.5"
      >
        {brandProfile.views.map((candidate) => {
          const isActive = candidate === view;
          const Icon = candidate === 'calendar' ? CalendarDays : ListIcon;
          return (
            <button
              key={candidate}
              role="tab"
              aria-selected={isActive}
              onClick={() => setViewByBrand((prev) => ({ ...prev, [activeBrand]: candidate }))}
              className="flex items-center gap-1.5 rounded-md px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm font-medium transition-colors"
              style={
                isActive
                  ? { backgroundColor: brandProfile.accent, color: '#fff' }
                  : { color: '#4b5563' }
              }
            >
              <Icon className="h-4 w-4" />
              <span className="capitalize">{candidate}</span>
            </button>
          );
        })}
      </div>
    </div>
  ) : null;

  /* Same constraint as the catalog header: in the list view it has to follow the fixed
     category nav's spacer, or the nav sits on top of it and hides the first lines of the
     notice. Kept a `fixed` nav rather than `sticky` because Layout's pull-to-refresh wrapper
     is `overflow: hidden`, which stops a sticky nav from ever sticking. */
  const campBanner = campNudge.showBanner ? (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
      <CampPaymentNudge invoices={campNudge.invoices} variant="banner" onDismiss={campNudge.dismiss} />
    </div>
  ) : null;

  return (
    <>
    {campNudge.showBlocking && (
      <CampPaymentNudge
        invoices={campNudge.invoices}
        onDismiss={campNudge.dismiss}
      />
    )}
    <div className={`${
      isMobile ? 'py-4' : 'py-8'
    }`}>
      {view === 'calendar' ? (
        <>
        {campBanner}
        <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
          {catalogHeader}
          <ProgramCalendarView
            programs={programs.map((program) => ({ ...toCalendarProgram(program), full: isProgramFull(capacityById.get(program.id)) }))}
            brand={activeBrand}
          />
        </div>
        </>
      ) : (
      <>
      {/* Sticky horizontal category nav (fixed below header) */}
      <div
        className="fixed z-40 left-0 right-0 bg-white border-b border-gray-200"
        style={{ top: stickyTop }}
      >
        <div className="max-w-7xl mx-auto relative">
          {canScrollLeft && (
            <>
              <div className="pointer-events-none absolute inset-y-0 left-0 w-14 bg-gradient-to-r from-white via-white/80 to-transparent z-10" />
              <button
                onClick={() => nudgeNav('left')}
                aria-label="Scroll left"
                className="absolute inset-y-0 left-0 z-20 flex items-center justify-center w-10 text-gray-500 hover:text-gray-900 transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            </>
          )}
          <nav ref={stickyNavRef} className="flex overflow-x-auto no-scrollbar px-3 sm:px-6">
            {typesOrder.map((type) => {
              const isActive = activeType ? activeType === type : typesOrder[0] === type;
              return (
                <button
                  key={type}
                  onClick={() => scrollToType(type)}
                  className={`flex-shrink-0 py-3 px-3 sm:px-4 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                    isActive
                      ? 'border-[color:var(--brand-accent)] text-[color:var(--brand-accent)]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {programTypeName(type)}
                </button>
              );
            })}
          </nav>
          {canScrollRight && (
            <>
              <div className="pointer-events-none absolute inset-y-0 right-0 w-14 bg-gradient-to-l from-white via-white/80 to-transparent z-10" />
              <button
                onClick={() => nudgeNav('right')}
                aria-label="Scroll right"
                className="absolute inset-y-0 right-0 z-20 flex items-center justify-center w-10 text-gray-500 hover:text-gray-900 transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      </div>
      {/* Spacer to offset fixed nav height */}
      <div style={{ height: stickyNavHeight }} />

      {/* Below the spacer on purpose: the category nav above is `fixed`, so anything rendered
          before the spacer is covered by it. */}
      {campBanner}
      <div className="px-3 sm:px-6 lg:px-8">{catalogHeader}</div>

      <div className="space-y-6 sm:space-y-8">
        {sortedEntries.map(([type, typePrograms], index) => (
          <div
            key={type}
            ref={(el) => (sectionRefs.current[type] = el)}
            data-type={type}
            style={{ scrollMarginTop: stickyTop + stickyNavHeight + 16 }}
            className="space-y-4 sm:space-y-6"
          >
            {/* Section header with solid brand color background */}
            <div className="relative">
              <div
                className="absolute inset-0 rounded-2xl"
                style={{ backgroundColor: getProgramTypeColor(type) }}
              ></div>

              <div
                className="relative px-3 sm:px-6 py-3 sm:py-4"
              >
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center bg-white bg-opacity-25">
                      {brandProfile.bilingual ? (
                        <SectionGlyph
                          char={getProgramTypeGlyph(type)}
                          className="text-sm sm:text-lg text-white"
                        />
                      ) : (
                        <span aria-hidden="true" className="text-sm sm:text-lg">
                          {getProgramTypeIcon(type)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-base sm:text-xl font-bold text-white truncate">
                      {/* English only — the glyph badge beside it already carries the 中文 */}
                      {getProgramTypeDisplayName(type as ProgramType)}
                    </h2>
                    <p className="text-xs sm:text-sm text-white text-opacity-80">
                      {typePrograms.length} program{typePrograms.length !== 1 ? 's' : ''} available
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Programs grid for this type - grouped by tag if any programs have tags */}
            {(() => {
              const hasAnyTags = typePrograms.some(p => p.tags && p.tags.length > 0);
              if (!hasAnyTags) {
                return (
                  <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                    {typePrograms.map((program) => (
                      <ProgramCard key={program.id} program={program} isFull={isProgramFull(capacityById.get(program.id))} />
                    ))}
                  </div>
                );
              }
              // Collect unique tags in stable order (order of first appearance)
              const tagOrder: string[] = [];
              const tagMap = new Map<string, ProgramWithPricing[]>();
              const untagged: ProgramWithPricing[] = [];
              typePrograms.forEach((program) => {
                if (!program.tags || program.tags.length === 0) {
                  untagged.push(program);
                } else {
                  program.tags.forEach((tag) => {
                    if (!tagMap.has(tag)) {
                      tagOrder.push(tag);
                      tagMap.set(tag, []);
                    }
                    tagMap.get(tag)!.push(program);
                  });
                }
              });
              tagOrder.sort((a, b) => a.localeCompare(b));
              tagMap.forEach((progs) => progs.sort((a, b) => a.name.localeCompare(b.name)));
              untagged.sort((a, b) => a.name.localeCompare(b.name));
              return (
                <div className="space-y-6">
                  {untagged.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                      {untagged.map((program) => (
                        <ProgramCard key={program.id} program={program} isFull={isProgramFull(capacityById.get(program.id))} />
                      ))}
                    </div>
                  )}
                  {tagOrder.map((tag) => (
                    <div key={tag} className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="h-px flex-1 bg-gray-200" />
                        <span className="text-xs font-semibold uppercase tracking-widest text-gray-500 px-2">
                          {tag}
                        </span>
                        <div className="h-px flex-1 bg-gray-200" />
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                        {tagMap.get(tag)!.map((program) => (
                          <ProgramCard key={`${tag}-${program.id}`} program={program} isFull={isProgramFull(capacityById.get(program.id))} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            {/* Decorative separator between sections (except for the last one) */}
            {index < sortedEntries.length - 1 && (
              <div className="flex items-center justify-center">
                <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                <div className="mx-2 sm:mx-4 w-2 h-2 bg-gray-300 rounded-full"></div>
                <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
              </div>
            )}
          </div>
        ))}
      </div>
      </>
      )}
    </div>
    </>
  );
}